import { Clear } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import React, { useEffect, useRef } from 'react';

export type CustomTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
    value?: string | null;
    onChange?: (
        value: string,
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => void;
    onClear?: () => void;
    clearable?: boolean | 'whenNotEmpty' | 'always';
    selectOnFocus?: boolean;
    validate?: ((value: string) => boolean) | RegExp;
    readOnly?: boolean;
};

export function CustomTextField({
    value,
    onChange,
    onClear,
    clearable = !!onClear,
    selectOnFocus,
    focused,
    validate,
    readOnly,
    ...props
}: CustomTextFieldProps) {
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (focused) {
            ref.current?.focus();
        }
    }, [focused]);

    const canClear = clearable && (clearable === 'always' || !!value);

    return (
        <TextField
            {...props}
            inputRef={ref}
            value={value ?? ''}
            onChange={(e) => {
                const value = e.target.value;

                if ((validate instanceof Function && !validate(value)) || (validate instanceof RegExp && !validate.test(value))) {
                    return;
                }

                onChange?.(value, e);
            }}
            onFocus={
                selectOnFocus
                    ? (e) => {
                          e.target.select();
                          props.onFocus?.(e);
                      }
                    : props.onFocus
            }
            InputProps={{
                ...props.InputProps,
                endAdornment: canClear ? (
                    <InputAdornment position="end" sx={{ mr: -1.5 }}>
                        <IconButton
                            color="inherit"
                            size="small"
                            onClick={(e) => {
                                onChange?.('', e);
                                onClear?.();
                            }}
                        >
                            <Clear fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ) : (
                    props.InputProps?.endAdornment
                ),
                readOnly: props.InputProps?.readOnly ?? readOnly,
            }}
        />
    );
}
